import { API_V1 } from '../config'
import { apiFetch } from './api'

// ============================================================
// Types — match backend DocumentTemplate + TemplateVariable models
// ============================================================

export type TemplateCategory =
  | 'hr'
  | 'sales'
  | 'legal'
  | 'real_estate'
  | 'finance'
  | 'internal'
  | 'other'

export type TemplateContentType = 'pdf' | 'html' | 'markdown' | 'docx'

export type VariableFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'date'
  | 'email'
  | 'phone'
  | 'select'
  | 'checkbox'
  | 'signature'

/** List view shape (DocumentTemplateListSerializer) */
export interface TemplateListItem {
  id: number
  name: string
  slug: string
  description: string
  category: TemplateCategory | null
  content_type: TemplateContentType
  purpose: TemplatePurpose
  version: string
  is_published: boolean
  is_system_template: boolean
  variable_count: number
  signature_field_count: number
  organization_name: string | null
  created_by_name: string | null
  departments: Array<{ id: number; name: string; color: string }>
  created_at: string
  updated_at: string
}

/** Variable definition */
export interface TemplateVariable {
  id: number
  name: string
  label: string
  description: string
  field_type: VariableFieldType
  is_required: boolean
  min_length: number | null
  max_length: number | null
  min_value: number | null
  max_value: number | null
  regex_pattern: string
  options: Array<{ value: string; label: string }>
  default_value: string
  order: number
  group: string
}

/** Signature field placement */
export interface TemplateSignatureField {
  id: number
  name: string
  label: string
  signer_type: 'sender' | 'recipient' | 'variable'
  signer_variable: string
  page: number
  x_position: number
  y_position: number
  width: number
  height: number
  signing_order: number
}

/** Template section definition (Sprint 4 — unified template engine) */
export interface TemplateSection {
  id: number
  key: string
  label: string
  description: string
  default_enabled: boolean
  order: number
}

/** Purpose filter for template listing */
export type TemplatePurpose = 'signing' | 'negotiation' | 'both'

/** Detail view shape (DocumentTemplateDetailSerializer) */
export interface TemplateDetail {
  id: number
  organization: number | null
  organization_name: string | null
  name: string
  slug: string
  description: string
  category: TemplateCategory | null
  content_type: TemplateContentType
  version: string
  is_published: boolean
  is_system_template: boolean
  default_signature_level: 'email' | 'passkey' | 'bankid'
  variables: TemplateVariable[]
  signature_fields: TemplateSignatureField[]
  version_history: Array<{ id: number; version: string; created_at: string }>
  departments: Array<{ id: number; name: string; color: string }>
  created_by_name: string | null
  created_at: string
  updated_at: string
  /** Sprint 4 fields — unified template engine */
  sections: TemplateSection[]
  purpose: TemplatePurpose
  language: string
  country: string
  default_deadline_days: number | null
  party_roles: string[]
}

/** Backend may return paginated response (DRF default pagination) or flat array */
export interface PaginatedTemplateResponse {
  count: number
  next: string | null
  previous: string | null
  results: TemplateListItem[]
}

export type TemplateListResponse = TemplateListItem[]

/** Request body for generate/generate-async */
export interface GenerateRequest {
  values: Record<string, string | number | boolean>
  signers: Array<{
    email: string
    name: string
    role?: 'sender' | 'recipient'
  }>
  title?: string
  message?: string
  send_immediately?: boolean
  expiry_days?: number
  signature_level?: 'SES' | 'AES'
}

/** Response from generate-async (202) */
export interface GenerateAsyncResponse {
  task_id: string
  status: 'queued'
  sse_url: string
}

/** SSE event data for generation progress */
export interface GenerationSSEEvent {
  type: 'generation_started' | 'generation_progress' | 'generation_complete' | 'generation_failed'
  data: {
    message?: string
    progress?: number
    koblink_id?: string
    download_url?: string
    error?: string
  }
}

// ============================================================
// API functions
// ============================================================

/** Fetch list of templates (paginated, filtered by organization and purpose) */
export async function getTemplates(params?: {
  search?: string
  category?: string
  page?: number
  organizationId?: string
  purpose?: TemplatePurpose
}): Promise<TemplateListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.set('search', params.search)
  if (params?.category) searchParams.set('category', params.category)
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.purpose) searchParams.set('purpose', params.purpose)

  const query = searchParams.toString()
  const url = `${API_V1}/documents/templates/${query ? `?${query}` : ''}`

  const headers: Record<string, string> = {}
  if (params?.organizationId) {
    headers['X-Organization'] = params.organizationId
  }

  const response = await apiFetch(url, { headers })

  if (!response.ok) {
    throw new Error(`Failed to fetch templates: ${response.status}`)
  }

  const json = await response.json()

  // Handle both paginated (DRF default) and flat array responses
  if (Array.isArray(json)) {
    return json
  }
  if (json && Array.isArray(json.results)) {
    return json.results
  }
  return []
}

/** Fetch a single template with full details + variables */
export async function getTemplate(id: number, organizationId?: string): Promise<TemplateDetail> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${id}/`, { headers })
  if (!response.ok) {
    throw new Error(`Failed to fetch template: ${response.status}`)
  }
  return response.json()
}

/** Preview a generated document — returns PDF binary (open in browser) */
export async function previewTemplate(
  id: number,
  values: Record<string, string | number | boolean>,
  useSampleData = false,
): Promise<Blob> {
  const response = await apiFetch(`${API_V1}/documents/templates/${id}/preview/`, {
    method: 'POST',
    body: JSON.stringify({ values, use_sample_data: useSampleData }),
  })
  if (!response.ok) {
    throw new Error(`Failed to preview template: ${response.status}`)
  }
  return response.blob()
}

/** Render a DOCX template with variables substituted — returns DOCX binary */
export async function renderDocxTemplate(
  id: number,
  values: Record<string, string | number | boolean>,
  organizationId?: string,
): Promise<Blob> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${id}/render-docx/`, {
    method: 'POST',
    body: JSON.stringify({ values }),
    headers,
  })
  if (!response.ok) {
    throw new Error(`Failed to render template: ${response.status}`)
  }
  return response.blob()
}

/** Generate a document from a template (async — returns SSE URL for progress) */
export async function generateFromTemplate(
  id: number,
  data: GenerateRequest,
): Promise<GenerateAsyncResponse> {
  const response = await apiFetch(`${API_V1}/documents/templates/${id}/generate-async/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    throw new Error(`Failed to generate document: ${response.status}`)
  }
  return response.json()
}

/** Create an EventSource for generation progress SSE */
export function createGenerationEventSource(sseUrl: string): EventSource {
  // SSE URL is relative — prefix with API base
  const fullUrl = sseUrl.startsWith('http') ? sseUrl : `${API_V1}${sseUrl}`
  return new EventSource(fullUrl, { withCredentials: true })
}

/** Response from creating a template */
export interface CreateTemplateResponse {
  id: number
  name: string
  slug: string
  category: TemplateCategory | null
  content_type: TemplateContentType
  is_published: boolean
}

/** Response from extract-variables */
export interface ExtractVariablesResponse {
  found: string[]
  created: number
  variables: TemplateVariable[]
}

/** Create a template from a DOCX file */
export async function createTemplate(params: {
  file: Blob
  filename: string
  name: string
  description?: string
  category?: TemplateCategory
  organizationId?: string
}): Promise<CreateTemplateResponse> {
  const formData = new FormData()
  formData.append('content_file', params.file, params.filename)
  formData.append('name', params.name)
  formData.append('content_type', 'docx')
  if (params.description) formData.append('description', params.description)
  if (params.category) formData.append('category', params.category)

  const headers: Record<string, string> = {}
  if (params.organizationId) {
    headers['X-Organization'] = params.organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/`, {
    method: 'POST',
    body: formData,
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.detail || `Failed to create template: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Update a single template variable (label, field_type, is_required, etc.) */
export async function updateTemplateVariable(
  templateId: number,
  variableId: number,
  data: Partial<Pick<TemplateVariable, 'label' | 'field_type' | 'is_required' | 'description' | 'default_value' | 'order' | 'group'>>,
  organizationId?: string,
): Promise<TemplateVariable> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${templateId}/variables/${variableId}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.detail || `Failed to update variable: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

// ============================================================
// Sprint 4 — Negotiation generation + Context fill
// ============================================================

/** Request body for generate-negotiation endpoint */
export interface GenerateNegotiationRequest {
  variables: Record<string, string>
  sections: Record<string, boolean>
  title?: string
  deadline_days?: number
  parties?: Array<{
    actor_id?: string
    party_label: string
    tag_name: string
    role: string
  }>
}

/** Response from generate-negotiation endpoint */
export interface GenerateNegotiationResponse {
  negotiation_id: string
  version: number
}

/** Request body for context-fill (AI suggestions) */
export interface ContextFillRequest {
  parties?: Array<{ name: string; role: string; org_number?: string }>
  additional_context?: string
}

/** Response from context-fill endpoint */
export interface ContextFillResponse {
  suggestions: Record<string, {
    value: string
    confidence: number
    source: string
  }>
}

/** Generate a negotiation from a template */
export async function generateNegotiation(
  templateId: string,
  data: GenerateNegotiationRequest,
  organizationId?: string,
): Promise<GenerateNegotiationResponse> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${templateId}/generate-negotiation/`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.detail || `Failed to generate negotiation: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Get AI-powered variable suggestions based on party context */
export async function contextFill(
  templateId: string,
  data: ContextFillRequest,
  organizationId?: string,
): Promise<ContextFillResponse> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${templateId}/context-fill/`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.detail || `Failed to get context fill: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Extract {{variable}} placeholders from a template */
export async function extractVariables(
  templateId: number,
  organizationId?: string,
): Promise<ExtractVariablesResponse> {
  const headers: Record<string, string> = {}
  if (organizationId) {
    headers['X-Organization'] = organizationId
  }

  const response = await apiFetch(`${API_V1}/documents/templates/${templateId}/extract-variables/`, {
    method: 'POST',
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.detail || `Failed to extract variables: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}
